import React, { useState } from 'react';
import { X, User, Bell, Volume2, Monitor, Palette, Shield, LogOut, ChevronRight } from 'lucide-react';
import './Settings.css';

const settingsSections = [
  { id: 'account', label: 'My Account', icon: User },
  { id: 'notifications', label: 'Notifications', icon: Bell },
  { id: 'voice', label: 'Voice & Video', icon: Volume2 },
  { id: 'appearance', label: 'Appearance', icon: Palette },
  { id: 'accessibility', label: 'Accessibility', icon: Monitor },
  { id: 'privacy', label: 'Privacy & Safety', icon: Shield },
];

function Settings({ settings, onSave, onClose }) {
  const [activeSection, setActiveSection] = useState('account');
  const [localSettings, setLocalSettings] = useState({ ...settings });

  const handleChange = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    onSave(localSettings);
    onClose();
  };

  const renderSection = () => {
    switch (activeSection) {
      case 'account':
        return (
          <div className="settings-section">
            <h2>My Account</h2>
            <div className="settings-card">
              <div className="profile-banner" />
              <div className="profile-content">
                <div className="profile-avatar">
                  {localSettings.username[0].toUpperCase()}
                </div>
                <div className="profile-info">
                  <h3>{localSettings.username}</h3>
                  <span className="profile-tag">#{Math.floor(Math.random() * 9000) + 1000}</span>
                </div>
              </div>
            </div>
            
            <div className="settings-group">
              <label className="settings-label">Username</label>
              <input
                type="text"
                className="settings-input"
                value={localSettings.username}
                onChange={(e) => handleChange('username', e.target.value)}
              />
            </div>

            <div className="settings-group">
              <label className="settings-label">Status</label>
              <select
                className="settings-select"
                value={localSettings.status}
                onChange={(e) => handleChange('status', e.target.value)}
              >
                <option value="online">Online</option>
                <option value="idle">Idle</option>
                <option value="dnd">Do Not Disturb</option>
                <option value="invisible">Invisible</option>
              </select>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="settings-section">
            <h2>Notifications</h2>
            <div className="settings-group">
              <div className="settings-toggle-row">
                <div>
                  <label className="settings-label">Enable Notifications</label>
                  <p className="settings-description">Receive desktop notifications for messages</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={localSettings.notifications}
                    onChange={(e) => handleChange('notifications', e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>

            <div className="settings-group">
              <div className="settings-toggle-row">
                <div>
                  <label className="settings-label">Sound Effects</label>
                  <p className="settings-description">Play sounds for notifications and actions</p>
                </div>
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={localSettings.soundEnabled}
                    onChange={(e) => handleChange('soundEnabled', e.target.checked)}
                  />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
          </div>
        );

      case 'voice':
        return (
          <div className="settings-section">
            <h2>Voice & Video</h2>
            <div className="settings-group">
              <label className="settings-label">Input Device</label>
              <select
                className="settings-select"
                value={localSettings.inputDevice}
                onChange={(e) => handleChange('inputDevice', e.target.value)}
              >
                <option value="default">Default - Microphone</option>
                <option value="mic1">Microphone (USB Audio)</option>
                <option value="mic2">Headset Microphone</option>
              </select>
            </div>

            <div className="settings-group">
              <label className="settings-label">Output Device</label>
              <select
                className="settings-select"
                value={localSettings.outputDevice}
                onChange={(e) => handleChange('outputDevice', e.target.value)}
              >
                <option value="default">Default - Speakers</option>
                <option value="speakers">Speakers (Realtek Audio)</option>
                <option value="headphones">Headphones</option>
              </select>
            </div>

            <div className="settings-group">
              <label className="settings-label">Microphone Volume</label>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={localSettings.microphoneVolume}
                  onChange={(e) => handleChange('microphoneVolume', parseInt(e.target.value))}
                  className="settings-slider"
                />
                <span className="slider-value">{localSettings.microphoneVolume}%</span>
              </div>
            </div>

            <div className="settings-group">
              <label className="settings-label">Speaker Volume</label>
              <div className="slider-container">
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={localSettings.speakerVolume}
                  onChange={(e) => handleChange('speakerVolume', parseInt(e.target.value))}
                  className="settings-slider"
                />
                <span className="slider-value">{localSettings.speakerVolume}%</span>
              </div>
            </div>
          </div>
        );

      case 'appearance':
        return (
          <div className="settings-section">
            <h2>Appearance</h2>
            <div className="settings-group">
              <label className="settings-label">Theme</label>
              <div className="theme-options">
                <div 
                  className={`theme-option dark ${localSettings.theme === 'dark' ? 'active' : ''}`}
                  onClick={() => handleChange('theme', 'dark')}
                >
                  <div className="theme-preview dark-preview" />
                  <span>Dark</span>
                </div>
                <div 
                  className={`theme-option ${localSettings.theme === 'light' ? 'active' : ''}`}
                  onClick={() => handleChange('theme', 'light')}
                >
                  <div className="theme-preview light-preview" />
                  <span>Light</span>
                </div>
                <div 
                  className={`theme-option ${localSettings.theme === 'midnight' ? 'active' : ''}`}
                  onClick={() => handleChange('theme', 'midnight')}
                >
                  <div className="theme-preview midnight-preview" />
                  <span>Midnight</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'accessibility':
        return (
          <div className="settings-section">
            <h2>Accessibility</h2>
            <p className="settings-description">
              Customize FEK to work better for you.
            </p>
            <div className="settings-group">
              <div className="settings-toggle-row">
                <div>
                  <label className="settings-label">Reduce Motion</label>
                  <p className="settings-description">Minimize animations throughout the app</p>
                </div>
                <label className="toggle">
                  <input type="checkbox" />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
          </div>
        );

      case 'privacy':
        return (
          <div className="settings-section">
            <h2>Privacy & Safety</h2>
            <div className="settings-group">
              <div className="settings-toggle-row">
                <div>
                  <label className="settings-label">Allow Direct Messages</label>
                  <p className="settings-description">Allow other users to send you direct messages</p>
                </div>
                <label className="toggle">
                  <input type="checkbox" defaultChecked />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="settings-overlay">
      <div className="settings-modal">
        <div className="settings-sidebar">
          <div className="settings-nav">
            {settingsSections.map((section) => (
              <button
                key={section.id}
                className={`settings-nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <section.icon size={18} />
                <span>{section.label}</span>
                <ChevronRight size={16} className="nav-arrow" />
              </button>
            ))}
          </div>
          <div className="settings-footer">
            <button className="logout-btn">
              <LogOut size={18} />
              <span>Log Out</span>
            </button>
            <div className="app-info">
              <span className="app-name">FEK</span>
              <span className="app-version">v1.0.0</span>
            </div>
          </div>
        </div>

        <div className="settings-content">
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
          {renderSection()}
          <div className="settings-actions">
            <button className="cancel-btn" onClick={onClose}>Cancel</button>
            <button className="save-btn" onClick={handleSave}>Save Changes</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;
